import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT, Bi } from "@/lib/i18n";
import { inr, todayISO } from "@/lib/format";
import { useApi } from "@/lib/useApi";
import { BottomSheet } from "@/components/BottomSheet";
import { Field, BigInput, MoneyInput } from "@/components/form";

const ID_TYPES = ["none", "aadhaar", "pan", "dl", "passport", "voter", "other"];
const ID_LABEL: Record<string, string> = { aadhaar: "Aadhaar", pan: "PAN", dl: "Driving licence", passport: "Passport", voter: "Voter ID", other: "Other" };

export function AddTenantSheet({ open, onClose, onSaved, bedId, prefill }: { open: boolean; onClose: () => void; onSaved: (t: any) => void; bedId?: string; prefill?: any }) {
  const { t } = useT();
  const { property } = useAuth();
  const rooms = useApi<any[]>(open && property ? `/properties/${property.id}/rooms` : null);
  const empty = { name: "", phone: "", bed_id: bedId || "", monthly_rent: 0, deposit: 0, move_in_date: todayISO(), id_type: "none", id_last4: "", consent: false, email: "", join_request_id: null };
  const [f, setF] = useState<any>(empty);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const p = { ...(prefill || {}) };
    if (p.move_in_date && p.move_in_date > new Date(Date.now() + 59 * 864e5).toISOString().slice(0, 10)) delete p.move_in_date;
    setF({ ...empty, ...p, bed_id: bedId || "" });
  }, [open, bedId]); // eslint-disable-line react-hooks/exhaustive-deps
  const vacant = useMemo(() => (rooms.data || []).flatMap((r) => r.beds.filter((b) => b.status === "vacant").map((b) => ({ ...b, room: r }))), [rooms.data]);
  useEffect(() => {
    const b = vacant.find((x) => x.id === f.bed_id);
    if (b) setF((p) => ({ ...p, monthly_rent: b.room.rent_per_bed, deposit: b.room.deposit }));
  }, [f.bed_id, vacant]);
  const day = Number(f.move_in_date.slice(8, 10));
  const showProrate = property?.proration_mode === "prorated" && day > 1;
  const valid = f.name.trim() && /^[6-9]\d{9}$/.test(f.phone) && f.bed_id && (f.id_type === "none" || (f.id_last4.length === 4 && f.consent));
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, property_id: property.id, id_last4: f.id_type === "none" ? null : f.id_last4, email: f.email.trim() || null };
      const r = await api.post("/tenants", body);
      toast.success(`${r.data.name} added`);
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <BottomSheet open={open} onOpenChange={(o) => !o && onClose()} title={<Bi k="add_tenant" />} testId="add-tenant-sheet">
      <div className="space-y-4">
        <Field label={t("tenant_name")}><BigInput data-testid="tenant-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
        <Field label={t("phone_label")}>
          <BigInput data-testid="tenant-phone-input" inputMode="numeric" maxLength={10} value={f.phone} placeholder="10-digit mobile" onChange={(e) => setF({ ...f, phone: e.target.value.replace(/\D/g, "") })} />
        </Field>
        <Field label={`${t("email")} (${t("optional")})`} hint="Lets the resident log in to the Nexstays resident app with this email.">
          <BigInput data-testid="tenant-email-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="resident@example.com" />
        </Field>
        <Field label={t("select_bed")}>
          {rooms.loading ? <div className="h-12 rounded-xl bg-slate-100 animate-pulse" /> : vacant.length === 0 ? (
            <p data-testid="tenant-no-vacant-beds" className="text-sm text-rose-600">{t("no_vacant_beds")}</p>
          ) : (
            <Select value={f.bed_id} onValueChange={(v) => setF({ ...f, bed_id: v })}>
              <SelectTrigger data-testid="tenant-bed-select" className="h-12 rounded-xl bg-white"><SelectValue placeholder={t("select_bed")} /></SelectTrigger>
              <SelectContent>
                {vacant.map((b) => (
                  <SelectItem key={b.id} value={b.id} data-testid={`tenant-bed-option-${b.id}`}>{t("room")} {b.room.name} · {t("bed")} {b.label} · {inr(b.room.rent_per_bed)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("monthly_rent")}><MoneyInput data-testid="tenant-rent-input" value={f.monthly_rent} onChange={(v) => setF({ ...f, monthly_rent: v })} /></Field>
          <Field label={t("deposit")}><MoneyInput data-testid="tenant-deposit-input" value={f.deposit} onChange={(v) => setF({ ...f, deposit: v })} /></Field>
        </div>
        <Field label={t("move_in_date")} hint={showProrate ? `${t("prorated_note")}: ~${inr(estimate(f.monthly_rent, f.move_in_date))}` : undefined}>
          <BigInput data-testid="tenant-movein-input" type="date" value={f.move_in_date} onChange={(e) => setF({ ...f, move_in_date: e.target.value })} />
        </Field>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
          <p className="text-sm font-semibold"><Bi k="id_details" /></p>
          <Select value={f.id_type} onValueChange={(v) => setF({ ...f, id_type: v })}>
            <SelectTrigger data-testid="tenant-idtype-select" className="h-12 rounded-xl bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ID_TYPES.map((x) => <SelectItem key={x} value={x} data-testid={`tenant-idtype-${x}`}>{x === "none" ? t("id_none") : ID_LABEL[x]}</SelectItem>)}
            </SelectContent>
          </Select>
          {f.id_type !== "none" && (
            <>
              <BigInput data-testid="tenant-idlast4-input" maxLength={4} placeholder={t("id_last4")} value={f.id_last4} onChange={(e) => setF({ ...f, id_last4: e.target.value.replace(/[^A-Za-z0-9]/g, "") })} />
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <Checkbox data-testid="tenant-consent-checkbox" checked={f.consent} onCheckedChange={(v) => setF({ ...f, consent: !!v })} className="mt-0.5 h-5 w-5" />
                {t("consent_label")}
              </label>
            </>
          )}
        </div>
        <Button data-testid="tenant-save-btn" disabled={!valid || busy} onClick={save} className="w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("add_tenant")}
        </Button>
      </div>
    </BottomSheet>
  );
}

function estimate(rent: number, d: string) {
  const [y, m, day] = d.split("-").map(Number);
  const dim = new Date(y, m, 0).getDate();
  return Math.round((rent * (dim - day + 1)) / dim);
}

export { ID_LABEL };
